import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Plus, Search, Building2, CreditCard, Calendar, DollarSign, CheckCircle } from 'lucide-react';
import { PageSpinner } from '../../components/PageSpinner';
import { Pagination } from '../../components/Pagination';
import { formatDateUTC } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ITEMS_PER_PAGE = 15;

// Mirrors backend's config.get_currency_for_country
function getCurrencyForCountry(country) {
  const countryLower = (country || '').toLowerCase().trim();
  if (countryLower.includes('colombia')) return 'COP';
  if (countryLower.includes('chile')) return 'CLP';
  return 'USD';
}

// Mirrors backend's per-currency price lookup in admin_routes.create_membership
function getPriceForCurrency(membershipType, currency) {
  const precios = membershipType.precios || [];
  const match = precios.find((p) => p.currency_code === currency);
  const amount = match ? match.amount : 0;
  return amount || membershipType.valor || 0;
}

export default function AdminMemberships() {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Modal states
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [allCompanies, setAllCompanies] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedMembershipType, setSelectedMembershipType] = useState(null);
  const [activationData, setActivationData] = useState({
    valor: 0,
    moneda: 'COP',
    fecha_activacion: new Date().toISOString().split('T')[0]
  });
  const [step, setStep] = useState(1); // 1: search, 2: select membership, 3: confirm
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    fetchMemberships();
  }, []);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      const filtered = allCompanies.filter(c => 
        c.razon_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.nombre_fantasia?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.codigo_empresa?.includes(searchTerm)
      );
      setFilteredCompanies(filtered);
    } else {
      setFilteredCompanies([]);
    }
  }, [searchTerm, allCompanies]);

  const fetchMemberships = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const response = await axios.get(`${API_URL}/admin/memberships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberships(response.data);
    } catch (error) {
      toast.error('Error al cargar membresías');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompaniesAndTypes = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const [companiesRes, typesRes, membershipsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/membership-types`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/admin/memberships`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Filter companies that don't have active memberships
      const companiesWithActiveMembership = new Set(membershipsRes.data.map(m => m.company_id));
      const companiesWithoutMembership = companiesRes.data.filter(c => !companiesWithActiveMembership.has(c.id));
      
      setAllCompanies(companiesWithoutMembership);
      setMembershipTypes(typesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const openActivateModal = () => {
    setShowActivateModal(true);
    setStep(1);
    setSearchTerm('');
    setSelectedCompany(null);
    setSelectedMembershipType(null);
    setActivationData({
      valor: 0,
      moneda: 'COP',
      fecha_activacion: new Date().toISOString().split('T')[0]
    });
    fetchCompaniesAndTypes();
  };

  const selectCompany = (company) => {
    setSelectedCompany(company);
    setSearchTerm('');
    setFilteredCompanies([]);
    setStep(2);
  };

  const selectMembershipType = (type) => {
    const moneda = getCurrencyForCountry(selectedCompany?.pais);
    setSelectedMembershipType(type);
    setActivationData({
      ...activationData,
      valor: getPriceForCurrency(type, moneda),
      moneda
    });
    setStep(3);
  };

  const handleActivateMembership = async () => {
    if (!selectedCompany || !selectedMembershipType) return;
    
    setActivating(true);
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.post(`${API_URL}/admin/memberships`, {
        company_id: selectedCompany.id,
        membership_type_id: selectedMembershipType.id,
        fecha_activacion: activationData.fecha_activacion,
        valor: activationData.valor,
        moneda: activationData.moneda
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`¡Membresía activada para ${selectedCompany.razon_social || selectedCompany.nombre_fantasia}!`);
      setShowActivateModal(false);
      fetchMemberships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al activar membresía');
    } finally {
      setActivating(false);
    }
  };

  const goBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedCompany(null);
    } else if (step === 3) {
      setStep(2);
      setSelectedMembershipType(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Administrar Membresías</h1>
            </div>
          </div>
          <button 
            onClick={openActivateModal}
            className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-colors shadow-lg shadow-violet-200"
            data-testid="btn-activar-membresia"
          >
            <Plus size={20} />
            Activar Membresía
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8">
        {loading ? (
          <PageSpinner />
        ) : memberships.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <CreditCard className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No hay membresías activas</h3>
            <p className="text-slate-600 mb-6">Comienza activando una membresía para una empresa</p>
            <button 
              onClick={openActivateModal}
              className="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-colors"
            >
              <Plus size={20} />
              Activar Membresía
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Código</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Razón Social</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Documento</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha Activación</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha Renovación</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Tipo Membresía</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">Estado Pago</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {memberships.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((membership) => (
                  <tr key={membership.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-mono font-semibold text-violet-600">{membership.codigo_empresa}</td>
                    <td className="px-6 py-4 text-sm text-slate-900">{membership.razon_social}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{membership.numero_documento}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDateUTC(membership.fecha_activacion)}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDateUTC(membership.fecha_renovacion)}</td>
                    <td className="px-6 py-4"><span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold">{membership.tipo_membresia}</span></td>
                    <td className="px-6 py-4 text-center">
                      {membership.estado_pago === 'Vigente' ? (
                        <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">Vigente</span>
                      ) : (
                        <span className="px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">Pendiente</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => navigate(`/admin/membership/${membership.company_id}`)} 
                        className="flex items-center gap-1 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-sm font-semibold transition-colors"
                        data-testid={`btn-ver-detalle-${membership.company_id}`}
                      >
                        <Eye size={16} />
                        Ver detalle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4">
              <Pagination currentPage={currentPage} totalPages={Math.ceil(memberships.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </div>
          </div>
        )}
      </main>

      {/* Modal Activar Membresía */}
      {showActivateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-8 py-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {step > 1 && (
                  <button onClick={goBack} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ArrowLeft size={20} />
                  </button>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Activar Membresía</h2>
                  <p className="text-slate-600 text-sm">
                    {step === 1 && 'Paso 1: Buscar empresa'}
                    {step === 2 && 'Paso 2: Seleccionar membresía'}
                    {step === 3 && 'Paso 3: Confirmar y activar'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowActivateModal(false)} 
                className="text-slate-400 hover:text-slate-600 text-2xl font-light"
              >
                ×
              </button>
            </div>

            <div className="p-8">
              {/* Step 1: Search Company */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Buscar empresa por nombre o código
                    </label>
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                      <input 
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Escriba el nombre o código de la empresa..."
                        className="w-full pl-12 pr-4 py-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 text-lg"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Search Results */}
                  {filteredCompanies.length > 0 && (
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                        <p className="text-sm text-slate-600">{filteredCompanies.length} empresa(s) encontrada(s)</p>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {filteredCompanies.map((company) => (
                          <button
                            key={company.id}
                            onClick={() => selectCompany(company)}
                            className="w-full px-4 py-4 hover:bg-violet-50 border-b border-slate-100 last:border-b-0 text-left transition-colors"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <Building2 className="text-violet-600" size={24} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-slate-900 truncate">
                                  {company.razon_social || company.nombre_fantasia}
                                </p>
                                <p className="text-sm text-slate-500">
                                  Código: <span className="font-mono text-violet-600">{company.codigo_empresa}</span>
                                  {company.numero_documento && <span className="ml-3">Doc: {company.numero_documento}</span>}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {searchTerm.length >= 2 && filteredCompanies.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Building2 className="mx-auto mb-3 text-slate-300" size={40} />
                      <p>No se encontraron empresas sin membresía activa</p>
                    </div>
                  )}

                  {searchTerm.length < 2 && (
                    <div className="text-center py-8 text-slate-500">
                      <Search className="mx-auto mb-3 text-slate-300" size={40} />
                      <p>Escriba al menos 2 caracteres para buscar</p>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Select Membership Type */}
              {step === 2 && selectedCompany && (
                <div className="space-y-6">
                  {/* Selected Company Summary */}
                  <div className="bg-violet-50 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                      <Building2 className="text-violet-600" size={24} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{selectedCompany.razon_social || selectedCompany.nombre_fantasia}</p>
                      <p className="text-sm text-violet-600 font-mono">{selectedCompany.codigo_empresa}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-3">
                      Seleccione el tipo de membresía
                    </label>
                    <p className="text-xs text-slate-500 mb-3">
                      Precios en <span className="font-semibold">{getCurrencyForCountry(selectedCompany.pais)}</span> según el país de la empresa ({selectedCompany.pais || 'N/A'})
                    </p>
                    <div className="grid gap-3">
                      {membershipTypes.map((type) => {
                        const moneda = getCurrencyForCountry(selectedCompany.pais);
                        const precio = getPriceForCurrency(type, moneda);
                        return (
                          <button
                            key={type.id}
                            onClick={() => selectMembershipType(type)}
                            className="w-full p-4 border-2 border-slate-200 hover:border-violet-400 rounded-xl text-left transition-all hover:shadow-md"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-bold text-slate-900 text-lg">{type.nombre}</p>
                                <p className="text-sm text-slate-500">
                                  {type.cantidad_consentimientos === -1 ? 'Consentimientos ilimitados' : `${type.cantidad_consentimientos} consentimientos/mes`}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-emerald-600">${precio.toLocaleString()}</p>
                                <p className="text-xs text-slate-400">{moneda}/mes</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Confirm and Activate */}
              {step === 3 && selectedCompany && selectedMembershipType && (
                <div className="space-y-6">
                  {/* Company Detail */}
                  <div className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Building2 className="text-violet-600" size={24} />
                      <h3 className="font-bold text-slate-900">Detalle de la Empresa</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">Código</p>
                        <p className="font-mono font-semibold text-violet-600">{selectedCompany.codigo_empresa}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Razón Social</p>
                        <p className="font-semibold text-slate-900">{selectedCompany.razon_social || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Nombre Fantasía</p>
                        <p className="font-semibold text-slate-900">{selectedCompany.nombre_fantasia || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Documento</p>
                        <p className="font-mono text-slate-900">{selectedCompany.numero_documento || 'N/A'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-sm text-slate-500">Dirección</p>
                        <p className="text-slate-900">{selectedCompany.direccion || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">País</p>
                        <p className="text-slate-900">{selectedCompany.pais || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Membership Detail */}
                  <div className="bg-violet-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <CreditCard className="text-violet-600" size={24} />
                      <h3 className="font-bold text-slate-900">Membresía a Activar</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-violet-600">Tipo de Membresía</p>
                        <p className="text-xl font-bold text-slate-900">{selectedMembershipType.nombre}</p>
                      </div>
                      <div>
                        <p className="text-sm text-violet-600">Consentimientos</p>
                        <p className="text-xl font-bold text-slate-900">
                          {selectedMembershipType.cantidad_consentimientos === -1 ? 'Ilimitados' : selectedMembershipType.cantidad_consentimientos}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Editable Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <DollarSign size={16} className="inline mr-1" />
                        Valor de la Membresía ({activationData.moneda})
                      </label>
                      <input
                        type="number"
                        value={activationData.valor}
                        onChange={(e) => setActivationData({...activationData, valor: parseFloat(e.target.value)})}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        <Calendar size={16} className="inline mr-1" />
                        Fecha de Activación
                      </label>
                      <input 
                        type="date"
                        value={activationData.fecha_activacion}
                        onChange={(e) => setActivationData({...activationData, fecha_activacion: e.target.value})}
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Nota:</strong> La fecha de activación es desde la cual la empresa podrá utilizar las opciones del sistema. 
                      La fecha de renovación será 30 días después de la fecha de activación.
                    </p>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={handleActivateMembership}
                    disabled={activating}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg shadow-emerald-200"
                  >
                    {activating ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Activando...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={24} />
                        Guardar y Activar Membresía
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
